import { ARMOR_SLOT_BY_SUFFIX, NAMESPACE } from "./catalog";
import { toSnbt } from "./snbt";
import type { Creation, Mob, Stats, TimedEffect } from "./types";

type Components = Record<string, unknown>;

const PLAYER_BASE_ATTACK_DAMAGE = 1;
const PLAYER_BASE_ATTACK_SPEED = 4;
const DEFAULT_ATTACK_DAMAGE = 7;
const DEFAULT_ATTACK_SPEED = 1.6;
const DEFAULT_ARMOR_BY_SLOT: Record<string, number> = { head: 3, chest: 8, legs: 6, feet: 3 };
const NEVER_FINISHES_SECONDS = 100000;
const TICKS_PER_SECOND = 20;

export function giveCommand(creation: Creation): string {
  const components = Object.entries(itemComponents(creation)).map(([k, v]) => `${k}=${toSnbt(v)}`);
  return `give @s minecraft:${creation.baseItem}[${components.join(",")}] 1`;
}

export function itemComponents(creation: Creation): Components {
  const id = creation.id;
  const c: Components = { "minecraft:item_name": creation.name };
  if (creation.description) c["minecraft:lore"] = [{ text: creation.description, color: "gray", italic: false }];
  c["minecraft:rarity"] = creation.rarity;
  c["minecraft:item_model"] = `${NAMESPACE}:${id}`;
  c["minecraft:custom_data"] = { [NAMESPACE]: id };
  if (creation.glint) c["minecraft:enchantment_glint_override"] = true;
  if (creation.stats.durability) c["minecraft:max_damage"] = creation.stats.durability;
  const modifiers = attributeModifiers(creation);
  if (modifiers.length) c["minecraft:attribute_modifiers"] = modifiers;
  if (creation.hitPowers.length) {
    c["minecraft:enchantments"] = { [`${NAMESPACE}:${id}`]: 1 };
    c["minecraft:tooltip_display"] = { hidden_components: ["minecraft:enchantments"] };
  }
  if (creation.ability) {
    c["minecraft:consumable"] = {
      consume_seconds: NEVER_FINISHES_SECONDS,
      animation: "none",
      has_consume_particles: false,
      sound: "minecraft:intentionally_empty",
    };
  }
  if (creation.food) {
    c["minecraft:food"] = { nutrition: creation.food.nutrition, saturation: creation.food.saturation, can_always_eat: true };
    c["minecraft:consumable"] = {
      consume_seconds: 1.6,
      ...(creation.food.effects.length && {
        on_consume_effects: [{ type: "minecraft:apply_effects", effects: creation.food.effects.map(effectInstance) }],
      }),
    };
  }
  if (creation.mob) c["minecraft:entity_data"] = entityData(creation.name, creation.mob);
  return c;
}

export function entityData(name: string, mob: Mob): Record<string, unknown> {
  const data: Record<string, unknown> = {
    id: `minecraft:${mob.entity}`,
    CustomName: name,
    CustomNameVisible: true,
    PersistenceRequired: true,
  };
  if (mob.glowing) data.Glowing = true;
  data.Health = mob.health;
  data.attributes = [
    { id: "minecraft:max_health", base: mob.health },
    { id: "minecraft:scale", base: mob.scale },
    { id: "minecraft:attack_damage", base: mob.damage },
  ];
  if (mob.mainhand) {
    data.equipment = { mainhand: { id: `minecraft:${mob.mainhand}`, count: 1 } };
    data.drop_chances = { mainhand: 0 };
  }
  const effects = [...speedEffects(mob.speedLevel), ...mob.effects.map(({ effect, level }) => ({ effect, level }))];
  if (effects.length) {
    data.active_effects = effects.map(({ effect, level }) => ({
      id: `minecraft:${effect}`, amplifier: level - 1, duration: -1, show_particles: false,
    }));
  }
  return data;
}

function speedEffects(speedLevel: number): { effect: string; level: number }[] {
  if (speedLevel > 0) return [{ effect: "speed", level: speedLevel }];
  if (speedLevel < 0) return [{ effect: "slowness", level: -speedLevel }];
  return [];
}

function effectInstance(e: TimedEffect) {
  return { id: `minecraft:${e.effect}`, duration: e.seconds * TICKS_PER_SECOND, amplifier: e.level - 1 };
}

function attributeModifiers(creation: Creation) {
  const { kind, stats } = creation;
  if (kind === "armor") return armorModifiers(creation.id, stats, armorSlot(creation.baseItem));
  if (kind === "food" || kind === "mob") return [];
  return handModifiers(creation.id, stats);
}

function handModifiers(id: string, stats: Stats) {
  const modifiers = [];
  if (stats.attackDamage !== undefined || stats.attackSpeed !== undefined) {
    const damage = stats.attackDamage ?? DEFAULT_ATTACK_DAMAGE;
    const speed = stats.attackSpeed ?? DEFAULT_ATTACK_SPEED;
    modifiers.push(modifier(id, "attack_damage", damage - PLAYER_BASE_ATTACK_DAMAGE, "add_value", "mainhand"));
    modifiers.push(modifier(id, "attack_speed", round(speed - PLAYER_BASE_ATTACK_SPEED), "add_value", "mainhand"));
  }
  return [...modifiers, ...bonusModifiers(id, stats, "mainhand")];
}

function armorModifiers(id: string, stats: Stats, slot: string) {
  const modifiers = [modifier(id, "armor", stats.armor ?? DEFAULT_ARMOR_BY_SLOT[slot], "add_value", slot)];
  if (stats.armorToughness) modifiers.push(modifier(id, "armor_toughness", stats.armorToughness, "add_value", slot));
  return [...modifiers, ...bonusModifiers(id, stats, slot)];
}

function bonusModifiers(id: string, stats: Stats, slot: string) {
  const modifiers = [];
  if (stats.moveSpeed) modifiers.push(modifier(id, "movement_speed", stats.moveSpeed, "add_multiplied_base", slot, "move_speed"));
  if (stats.maxHealth) modifiers.push(modifier(id, "max_health", stats.maxHealth, "add_value", slot));
  return modifiers;
}

function modifier(id: string, attribute: string, amount: number, operation: string, slot: string, suffix = attribute) {
  return { type: `minecraft:${attribute}`, id: `${NAMESPACE}:${id}_${suffix}`, amount, operation, slot };
}

function armorSlot(baseItem: string): string {
  const suffix = baseItem.split("_").pop() ?? "";
  return ARMOR_SLOT_BY_SUFFIX[suffix] ?? "chest";
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
